import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { FiHome, FiBriefcase, FiFileText, FiCheckSquare, FiAlertCircle, FiCalendar, FiClock } from 'react-icons/fi';
import './Dashboard.css';

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('pt-BR');
};

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [proximosPrazos, setProximosPrazos] = useState([]);
  const [tarefasVencidas, setTarefasVencidas] = useState([]);
  const [licencasVencendo, setLicencasVencendo] = useState([]);
  const [ultimasTarefasConcluidas, setUltimasTarefasConcluidas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const response = await api.get('/api/relatorios/executivo');
      setStats(response.data.estatisticas);
      setProximosPrazos(response.data.proximosPrazos || []);
      setTarefasVencidas(response.data.tarefasVencidas || []);
      setLicencasVencendo(response.data.licencasVencendo || []);
      setUltimasTarefasConcluidas(response.data.ultimasTarefasConcluidas || []);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="dashboard-loading">
          <div className="loading-spinner" />
          <p>Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <h1 className="page-title">
          <FiHome className="page-title-icon" />
          Dashboard
        </h1>
        {user?.nome && (
          <p className="dashboard-welcome">
            Olá, <strong>{user.nome}</strong>! Aqui está o resumo do seu Green Check.
          </p>
        )}
      </header>

      <div className="stats-grid">
        <Link to="/empreendimentos" className="stat-card glass stat-card-link">
          <div className="stat-icon stat-icon-accent">
            <FiBriefcase />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats?.total_empreendimentos || 0}</div>
            <div className="stat-label">Empreendimentos</div>
          </div>
        </Link>

        <Link to="/licencas" className="stat-card glass stat-card-link">
          <div className="stat-icon stat-icon-accent">
            <FiFileText />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats?.total_licencas || 0}</div>
            <div className="stat-label">Licenças</div>
          </div>
        </Link>

        <Link to="/tarefas" className="stat-card glass stat-card-link">
          <div className="stat-icon stat-icon-success">
            <FiCheckSquare />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats?.tarefas_concluidas || 0}</div>
            <div className="stat-label">Tarefas Concluídas</div>
          </div>
        </Link>

        <Link to="/tarefas" className="stat-card glass stat-card-link">
          <div className="stat-icon stat-icon-danger">
            <FiAlertCircle />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats?.tarefas_vencidas || 0}</div>
            <div className="stat-label">Tarefas Vencidas</div>
          </div>
        </Link>
      </div>

      <div className="dashboard-sections">
        <section className="dashboard-section">
          <h2>
            <FiAlertCircle className="section-icon" />
            Tarefas vencidas
          </h2>
          <div className="glass dashboard-card">
            {tarefasVencidas.length > 0 ? (
              <div className="prazos-list">
                {tarefasVencidas.map((tarefa) => (
                  <Link key={tarefa.id} to={`/tarefas/kanban?tarefa=${tarefa.id}`} className="prazo-item prazo-item-link">
                    <div className="prazo-item-content">
                      <div className="fw-bold">{tarefa.titulo}</div>
                      <div className="text-muted prazo-item-desc">
                        {tarefa.condicionante_descricao?.substring(0, 60)}
                        {(tarefa.condicionante_descricao?.length || 0) > 60 ? '…' : ''}
                      </div>
                    </div>
                    <span className="badge badge-danger">
                      <FiClock /> Venceu {formatDate(tarefa.data_vencimento)}
                    </span>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="dashboard-empty">
                <FiCheckSquare className="dashboard-empty-icon" />
                <p>Nenhuma tarefa vencida</p>
                <Link to="/tarefas/kanban" className="btn btn-outline btn-sm">Ver tarefas</Link>
              </div>
            )}
          </div>
        </section>

        <section className="dashboard-section">
          <h2>
            <FiCalendar className="section-icon" />
            Próximos Prazos (7 dias)
          </h2>
          <div className="glass dashboard-card">
            {proximosPrazos.length > 0 ? (
              <div className="prazos-list">
                {proximosPrazos.map((tarefa) => (
                  <Link key={tarefa.id} to={`/tarefas/kanban?tarefa=${tarefa.id}`} className="prazo-item prazo-item-link">
                    <div className="prazo-item-content">
                      <div className="fw-bold">{tarefa.titulo}</div>
                      <div className="text-muted prazo-item-desc">
                        {tarefa.condicionante_descricao?.substring(0, 60)}
                        {(tarefa.condicionante_descricao?.length || 0) > 60 ? '…' : ''}
                      </div>
                    </div>
                    <span className="badge badge-warning">
                      <FiClock /> {formatDate(tarefa.data_vencimento)}
                    </span>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="dashboard-empty">
                <FiCalendar className="dashboard-empty-icon" />
                <p>Nenhum prazo nos próximos 7 dias</p>
                <Link to="/tarefas/kanban" className="btn btn-outline btn-sm">Ver tarefas</Link>
              </div>
            )}
          </div>
        </section>

        <section className="dashboard-section">
          <h2>
            <FiFileText className="section-icon" />
            Licenças vencendo (90 dias)
          </h2>
          <div className="glass dashboard-card">
            {licencasVencendo.length > 0 ? (
              <div className="licencas-list">
                {licencasVencendo.slice(0, 5).map((lic) => (
                  <Link key={lic.id} to={`/licencas/${lic.id}/validar`} className="licenca-item licenca-item-link">
                    <div className="licenca-item-content">
                      <div className="fw-bold">{lic.tipo} {lic.numero || ''}</div>
                      <div className="text-muted">{lic.empreendimento_nome}</div>
                    </div>
                    <span className="badge badge-warning">
                      Vence {formatDate(lic.data_vencimento)}
                    </span>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="dashboard-empty">
                <FiFileText className="dashboard-empty-icon" />
                <p>Nenhuma licença vencendo nos próximos 90 dias</p>
                <Link to="/licencas" className="btn btn-outline btn-sm">Ver licenças</Link>
              </div>
            )}
          </div>
        </section>

        <section className="dashboard-section">
          <h2>
            <FiCheckSquare className="section-icon" />
            Últimas tarefas concluídas
          </h2>
          <div className="glass dashboard-card">
            {ultimasTarefasConcluidas.length > 0 ? (
              <div className="prazos-list">
                {ultimasTarefasConcluidas.map((tarefa) => (
                  <Link key={tarefa.id} to={`/tarefas/kanban?tarefa=${tarefa.id}`} className="prazo-item prazo-item-link">
                    <div className="prazo-item-content">
                      <div className="fw-bold">{tarefa.titulo}</div>
                      <div className="text-muted prazo-item-desc">
                        {tarefa.condicionante_descricao?.substring(0, 60)}
                        {(tarefa.condicionante_descricao?.length || 0) > 60 ? '…' : ''}
                      </div>
                    </div>
                    <span className="badge badge-success">
                      <FiCheckSquare /> Concluída {tarefa.atualizado_em ? formatDate(tarefa.atualizado_em) : formatDate(tarefa.data_vencimento)}
                    </span>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="dashboard-empty">
                <FiCheckSquare className="dashboard-empty-icon" />
                <p>Nenhuma tarefa concluída recentemente</p>
                <Link to="/tarefas/kanban" className="btn btn-outline btn-sm">Ver tarefas</Link>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Dashboard;
